// @ts-nocheck
import { produce } from "immer";
import type { ActionType, AuthenticationStateType } from "../types";
import {
  AUTHENTICATE_USER_START,
  REGISTER_USER_DONE,
  REGISTER_USER_ERROR,
  LOGIN_USER_DONE,
  LOGIN_USER_ERROR,
  FORGOT_PASSWORD_DONE,
  FORGOT_PASSWORD_ERROR,
  SIGNOUT,
  UPDATE_USER_START,
  UPDATE_USER_ERROR,
  UPDATE_USER_DONE,
  UPDATE_FCM_TOKEN,
  INVALIDATE_SESSION,
  SMS_VERIFICATION,
  SMS_VERIFICATION_DONE,
  SMS_VERIFICATION_ERROR,
  RESET_SMS_VERIFICATION,
  RESEND_OTP,
  RESEND_OTP_ERROR,
  RESEND_OTP_DONE,
  CLEAR_OTP_ERRORS,
} from "../constants/actionTypes";

const AUTHENTICATION_STATE: AuthenticationStateType = {
  authenticating: false,
  authenticatedUserData: null,
  registerUserError: null,
  loginUserError: null,
  forgotPasswordEmailSent: false,
  forgotPasswordError: null,
  updatingUser: false,
  updateUserError: null,
  isFcmTokenUpdated: false,
  smsVerificationLoading: false,
  smsVerificationError: null,
  otpResendLoading: false,
  otpResendError: null,
  remainingOtpResendAttempts: 5,
};

const saveRegisteredUser = ({ action, draft }) => {
  draft.authenticating = false;
  draft.registerUserError = null;
  if (action.payload) {
    const { rememberToken, ...userData } = action.payload;
    draft.authenticatedUserData = userData;
  }
};

const saveRegisteredUserError = ({ action, draft }) => {
  draft.authenticating = false;
  draft.registerUserError = action.payload;
};

const saveLoggedInUser = ({ action, draft }) => {
  draft.authenticating = false;
  draft.loginUserError = null;
  if (action.payload) {
    const { rememberToken, ...userData } = action.payload;
    draft.authenticatedUserData = userData;
  }
};

const saveLoginUserError = ({ action, draft }) => {
  draft.authenticating = false;
  draft.loginUserError = action.payload;
};

const saveForgotPassword = ({ action, draft }) => {
  draft.authenticating = false;
  draft.loginUserError = null;
  if (action.payload) draft.forgotPasswordEmailSent = true;
};

const saveForgotPasswordError = ({ action, draft }) => {
  draft.authenticating = false;
  draft.forgotPasswordError = action.payload;
};

const authenticationStart = ({ draft }) => {
  draft.authenticating = true;
  draft.registerUserError = null;
  draft.loginUserError = null;
  draft.forgotPasswordError = null;
};

const signout = ({ draft }) => {
  draft.authenticating = false;
  draft.registerUserError = null;
  draft.loginUserError = null;
  draft.forgotPasswordError = null;
  draft.authenticatedUserData = null;
  draft.forgotPasswordEmailSent = false;
  draft.forgotPasswordError = null;
  draft.isFcmTokenUpdated = false;
  draft.otpResendLoading = false;
  draft.otpResendError = null;
  draft.remainingOtpResendAttempts = 5;
};

const updateUserStart = ({ draft }) => {
  draft.updatingUser = true;
  draft.updateUserError = null;
};

const updateUserError = ({ draft, action }) => {
  draft.updatingUser = false;
  draft.updateUserError = action.payload;
};

const updateUser = ({ draft, action }) => {
  draft.updatingUser = false;
  draft.authenticatedUserData = action.payload;
};

const updateFcmToken = ({ draft }) => {
  draft.isFcmTokenUpdated = true;
};

const smsVerification = ({ draft }) => {
  draft.smsVerificationLoading = true;
  draft.smsVerificationError = null;
};

const smsVerificationDone = ({ draft }) => {
  draft.smsVerificationLoading = false;
  draft.authenticatedUserData.isVerified = true;
  draft.authenticatedUserData.isIncomplete = false;
  draft.authenticatedUserData.telephone =
    draft.authenticatedUserData.verification.telephone;
};

const smsVerificationError = ({ draft, action }) => {
  draft.smsVerificationLoading = false;
  draft.smsVerificationError = action.payload;
};

const resetSmsVerification = ({ draft }) => {
  draft.authenticatedUserData.isVerified = false;
  draft.authenticatedUserData.isIncomplete = true;
};

const resendOtp = ({ draft }) => {
  draft.otpResendLoading = true;
  draft.otpResendError = null;
  draft.smsVerificationError = null;
};

const resendOtpError = ({ draft, action }) => {
  draft.otpResendLoading = false;
  draft.otpResendError = action.payload;
  draft.remainingOtpResendAttempts = 0;
};

const resendOtpDone = ({ draft, action: { payload: remainingAttempts } }) => {
  draft.otpResendLoading = false;
  draft.remainingOtpResendAttempts = remainingAttempts;
};

const clearOtpErrors = ({ draft }) => {
  draft.otpResendError = null;
  draft.smsVerificationError = null;
};

export default produce(
  (
    draft: AuthenticationStateType,
    action: ActionType,
  ): AuthenticationStateType => {
    switch (action.type) {
      case AUTHENTICATE_USER_START:
        authenticationStart({ draft });
        break;
      case REGISTER_USER_DONE:
        saveRegisteredUser({ action, draft });
        break;
      case REGISTER_USER_ERROR:
        saveRegisteredUserError({ action, draft });
        break;
      case LOGIN_USER_ERROR:
        saveLoginUserError({ action, draft });
        break;
      case LOGIN_USER_DONE:
        saveLoggedInUser({ action, draft });
        break;
      case FORGOT_PASSWORD_DONE:
        saveForgotPassword({ action, draft });
        break;
      case FORGOT_PASSWORD_ERROR:
        saveForgotPasswordError({ action, draft });
        break;
      case UPDATE_USER_START:
        updateUserStart({ action, draft });
        break;
      case UPDATE_USER_ERROR:
        updateUserError({ action, draft });
        break;
      case UPDATE_USER_DONE:
        updateUser({ action, draft });
        break;
      case UPDATE_FCM_TOKEN:
        updateFcmToken({ draft });
        break;
      case SMS_VERIFICATION:
        smsVerification({ draft });
        break;
      case SMS_VERIFICATION_DONE:
        smsVerificationDone({ draft, action });
        break;
      case SMS_VERIFICATION_ERROR:
        smsVerificationError({ draft, action });
        break;
      case RESET_SMS_VERIFICATION:
        resetSmsVerification({ draft });
        break;
      case RESEND_OTP:
        resendOtp({ draft });
        break;
      case RESEND_OTP_ERROR:
        resendOtpError({ draft, action });
        break;
      case RESEND_OTP_DONE:
        resendOtpDone({ draft, action });
        break;
      case CLEAR_OTP_ERRORS:
        clearOtpErrors({ draft });
        break;
      case INVALIDATE_SESSION:
      case SIGNOUT:
        signout({ action, draft });
        break;
      default:
      // noop
    }
  },
  AUTHENTICATION_STATE,
);
